import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api/api';
import AdminHeader from '../../components/adminHeader';
import AdminFooter from '../../components/adminFooter';
import EditUserModal from '../../components/modal/EditUserModal'; // Import the new modal
import CreateUserModal from '../../components/modal/CreateUserModal'; // Import the new create user modal
import ViewUserModal from '../../components/modal/ViewUserModal'; // Import the new view user modal

interface User {
  id: number;
  nickName: string;
  email: string;
  password_hash?: string;
  google_id?: string;
  name?: string;
  resetPasswordToken?: string;
  resetPasswordExpires?: Date;
  verified?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  role?: string;
  status?: 'Active' | 'Inactive';
  walletAddress?: string;
}

const ManageUser = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.nickName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '' });

  // State for the edit modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [userToView, setUserToView] = useState<User | null>(null);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/all/users'); // Fetch all users
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const handleCreateUser = async () => {
    try {
      await api.post('/api/all/users', newUser);
      setNewUser({ name: '', email: '', password: '' });
      fetchUsers(); // Refresh users
    } catch (error) {
      console.error('Failed to create user', error);
    }
  };

  const handleUpdateUser = async (id: number, updatedUser: any) => {
    try {
      await api.patch(`/api/all/users/${id}`, updatedUser);
      fetchUsers(); // Refresh users
    } catch (error) {
      console.error('Failed to update user', error);
    }
  };

  const handleDeleteUser = async (id: number) => {
    try {
      await api.delete(`/api/all/users/${id}`);
      fetchUsers(); // Refresh users
    } catch (error) {
      console.error('Failed to delete user', error);
    }
  };

  // Functions to open and close the modal
  const openEditModal = (user: User) => {
    setCurrentUser(user);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setCurrentUser(null);
  };

  const openViewModal = (user: User) => {
    setUserToView(user);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setUserToView(null);
  };

  useEffect(() => {
    fetchUsers();
  }, []); // Empty dependency array to fetch once on mount

  return (
    <div className="w-full h-full flex flex-col bg-[#00000071]">
      <AdminHeader />
      <div
        style={{
          backgroundImage: "url(assets/img/landing/landing-bg.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        className="flex-grow flex flex-col items-center justify-center w-full relative pt-6 px-4"
      >
        <div className="w-full max-w-6xl gothic-panel">
          <h1 className="gothic-heading">Manage Users</h1>
          <div className="flex justify-between mb-4">
            <button onClick={() => setIsCreateModalOpen(true)} className="gothic-button gothic-button-primary">
              Add New User
            </button>
            <input
              type="text"
              placeholder="Search users..."
              className="gothic-input w-1/3"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="overflow-x-auto mt-8">
            <table className="min-w-full gothic-panel border-collapse">
              <thead>
                <tr className="bg-gray-800">
                  <th className="py-3 px-4 text-left gothic-text-silver">ID</th>
                  <th className="py-3 px-4 text-left gothic-text-silver">Username</th>
                  <th className="py-3 px-4 text-left gothic-text-silver">Email</th>
                  <th className="py-3 px-4 text-left gothic-text-silver">Role</th>
                  <th className="py-3 px-4 text-left gothic-text-silver">Status</th>
                  <th className="py-3 px-4 text-left gothic-text-silver">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-t border-gray-700 hover:bg-gray-800 transition duration-200">
                    <td className="py-3 px-4 gothic-text-gold">{user.id}</td>
                    <td className="py-3 px-4 gothic-text-silver">{user.nickName || user.name || 'N/A'}</td>
                    <td className="py-3 px-4 gothic-text-silver">{user.email}</td>
                    <td className="py-3 px-4 gothic-text-silver">{user.role || 'N/A'}</td>
                    <td className="py-3 px-4 gothic-text-silver">{user.status || 'N/A'}</td>
                    <td className="py-3 px-4 flex flex-wrap gap-2">
                      <button onClick={() => openViewModal(user)} className="gothic-button bg-gray-700 border-gray-600">
                        View
                      </button>
                      <button onClick={() => openEditModal(user)} className="gothic-button bg-gray-700 border-gray-600">
                        Edit
                      </button>
                      <button onClick={() => handleDeleteUser(user.id)} className="gothic-button bg-gray-700 border-gray-600">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}n              </tbody>
            </table>
          </div>
        </div>
      </div>
      {isModalOpen && currentUser && (
        <EditUserModal
          isOpen={isModalOpen}
          onClose={closeEditModal}
          user={currentUser}
          onSave={handleUpdateUser}
        />
      )}
      {isCreateModalOpen && (
        <CreateUserModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateUser}
        />
      )}
      {isViewModalOpen && (
        <ViewUserModal
          isOpen={isViewModalOpen}
          onClose={closeViewModal}
          user={userToView}
        />
      )}
      <AdminFooter />
    </div>
  );
};

export default ManageUser;
